/**
 * Componente SubscriptionCard
 * Muestra la información de la suscripción actual del usuario
 */

import React, { useState, useMemo, useCallback } from 'react';
import { FiRefreshCw, FiSettings, FiCreditCard, FiAlertCircle, FiExternalLink } from 'react-icons/fi';
import Button from '../UI/Button';
import SubscriptionStatus from '../subscription/SubscriptionStatus';
import mercadoPagoService from '../../services/mercadopago_compatible';
import { supabase } from '../../lib/supabase';

/**
 * Componente SubscriptionCard
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.subscription - Datos de la suscripción
 * @param {boolean} props.isAuthenticated - Estado de autenticación
 * @param {boolean} props.loading - Estado de carga
 * @param {Function} props.onRefresh - Función para refrescar datos
 */
const SubscriptionCard = ({ 
  subscription, 
  isAuthenticated, 
  loading, 
  onRefresh 
}) => {
  // Estados para manejar las acciones de pago
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  // Debug: Mostrar datos recibidos (solo cuando cambian y son importantes)
  useMemo(() => {
    if (subscription && subscription.estado === 'pending' && !subscription.mp_preapproval_id) {
      console.log('🔍 Debug - SubscriptionCard con suscripción pendiente sin MP ID:', {
        id: subscription.id,
        estado: subscription.estado,
        mp_preapproval_id: subscription.mp_preapproval_id
      });
    }
  }, [subscription]);

  /**
   * Maneja el clic para continuar con el pago de una suscripción pendiente
   */
  const handleContinuePayment = useCallback(async () => {
    console.log('🔍 Debug - handleContinuePayment iniciado');
    console.log('📊 Datos de suscripción:', subscription);
    
    if (!subscription?.mp_preapproval_id) {
      console.error('❌ Error: mp_preapproval_id es nulo o undefined');
      console.log('📋 Subscription completo:', JSON.stringify(subscription, null, 2));
      setActionError('No se encontró una suscripción de Mercado Pago asociada. Contacta a soporte.');
      return;
    }

    console.log('🚀 Iniciando proceso de pago con MP ID:', subscription.mp_preapproval_id);
    
    setActionLoading(true);
    setActionError('');

    try {
      console.log('📞 Llamando a mercadoPagoService.continuePayment...');
      const result = await mercadoPagoService.continuePayment(
        subscription.id, 
        subscription.mp_preapproval_id
      );

      console.log('📥 Respuesta de continuePayment:', result);

      if (result.success) {
        console.log('✅ Pago exitoso, redirigiendo a:', result.paymentUrl);
        // Redirigir a Mercado Pago para continuar el pago
        window.location.href = result.paymentUrl;
      } else {
        console.error('❌ Error en continuePayment:', result.error);
        setActionError(result.error || 'Error al continuar con el pago');
      }
    } catch (error) {
      console.error('💥 Error crítico en handleContinuePayment:', error);
      console.log('📄 Stack trace:', error.stack);
      setActionError('Ocurrió un error al intentar continuar con el pago');
    } finally {
      console.log('🏁 Fin de handleContinuePayment');
      setActionLoading(false);
    }
  }, [subscription]);

  /**
   * Maneja la creación de una suscripción en Mercado Pago para una suscripción existente sin MP ID
   */
  const handleCreateMercadoPagoSubscription = useCallback(async () => {
    console.log('🔍 Debug - handleCreateMercadoPagoSubscription iniciado');
    console.log('📊 Datos de suscripción:', subscription);
    
    // Obtener el link del plan desde la API si no está disponible
    let planLink = subscription?.plan_mp_plan_link;
    
    if (!planLink) {
      console.log('📞 Obteniendo link del plan desde la API...');
      try {
        const { data: plan } = await supabase
          .from('planes')
          .select('mp_plan_link')
          .eq('id', subscription.plan_id)
          .single();
        
        planLink = plan?.mp_plan_link;
        
        if (!planLink) {
          console.error('❌ Error: No se encontró el link del plan en la base de datos');
          setActionError('No se encontró el link del plan de Mercado Pago. Contacta a soporte.');
          return;
        }
      } catch (error) {
        console.error('❌ Error obteniendo link del plan:', error);
        setActionError('Error al obtener información del plan. Intenta recargar la página.');
        return;
      }
    }

    // Construir URL con el nuevo retorno
    const baseUrl = planLink.split('?')[0];
    const params = new URLSearchParams(planLink.split('?')[1] || '');
    
    // Actualizar la URL de retorno para que use nuestra nueva ruta
    // const returnUrl = `${window.location.origin}/mp-return`;
    const returnUrl = `${window.location.origin}/suscripcion/return`;
    console.log('🔄 URL de retorno actualizada:', returnUrl);
    
    // Construir nueva URL con el retorno correcto
    const newUrl = `${baseUrl}?${params.toString()}&back_url=${encodeURIComponent(returnUrl)}`;
    
    console.log('🚀 Redirigiendo a Mercado Pago con URL modificada:', newUrl);
    
    setActionLoading(true);
    setActionError('');

    try {
      // Redirigir a Mercado Pago con la URL actualizada
      console.log('✅ Redirigiendo a Mercado Pago para crear suscripción...');
      window.location.href = newUrl;
      
    } catch (error) {
      console.error('💥 Error crítico en handleCreateMercadoPagoSubscription:', error);
      console.log('📄 Stack trace:', error.stack);
      setActionError('Ocurrió un error al intentar crear la suscripción en Mercado Pago');
    } finally {
      // Nota: No necesitamos setActionLoading(false) porque redirigimos
      console.log('🏁 Fin de handleCreateMercadoPagoSubscription');
    }
  }, [subscription]);

  /**
   * Maneja el clic para reintentar el pago de una suscripción fallida
   */
  const handleRetryPayment = useCallback(async () => {
    console.log('🔍 Debug - handleRetryPayment iniciado');
    console.log('📊 Datos de suscripción:', subscription);
    
    if (!subscription?.mp_preapproval_id) {
      console.error('❌ Error: mp_preapproval_id es nulo o undefined');
      console.log('📋 Subscription completo:', JSON.stringify(subscription, null, 2));
      setActionError('No se encontró una suscripción de Mercado Pago asociada. Contacta a soporte.');
      return;
    }

    console.log('🚀 Iniciando reintento de pago con MP ID:', subscription.mp_preapproval_id);
    
    setActionLoading(true);
    setActionError('');

    try {
      console.log('📞 Llamando a mercadoPagoService.retryPayment...');
      const result = await mercadoPagoService.retryPayment(
        subscription.id, 
        subscription.mp_preapproval_id
      );

      console.log('📥 Respuesta de retryPayment:', result);

      if (result.success) {
        console.log('✅ Reintento exitoso, redirigiendo a:', result.paymentUrl);
        // Redirigir a Mercado Pago para reintentar el pago
        window.location.href = result.paymentUrl;
      } else {
        console.error('❌ Error en retryPayment:', result.error);
        setActionError(result.error || 'Error al reintentar el pago');
      }
    } catch (error) {
      console.error('💥 Error crítico en handleRetryPayment:', error);
      console.log('📄 Stack trace:', error.stack);
      setActionError('Ocurrió un error al intentar reintentar el pago');
    } finally {
      console.log('🏁 Fin de handleRetryPayment');
      setActionLoading(false);
    }
  }, [subscription]);

  /**
   * Determina si mostrar botones de acción según el estado de la suscripción
   */
  const shouldShowActionButtons = useMemo(() => {
    if (!subscription) {
      return false;
    }
    
    const estado = subscription.estado;
    const tieneMpPreapprovalId = !!subscription.mp_preapproval_id;
    const tieneIntentosFallidos = subscription.intentos_pago > 0;
    const tieneErrorPago = subscription.ultimo_error_pago;

    // Mostrar botones si está pendiente (incluso sin MP ID) o si ha habido intentos fallidos
    const shouldShow = estado === 'pending' || tieneIntentosFallidos || tieneErrorPago;
    
    // Debug solo para casos importantes
    if (estado === 'pending' && !tieneMpPreapprovalId) {
      console.log('🔍 Debug - Mostrando botones para suscripción pendiente sin MP ID');
    }
    
    return shouldShow;
  }, [subscription]);

  /**
   * Obtiene el texto y acción apropiada según el estado
   */
  const actionButtonConfig = useMemo(() => {
    if (!subscription) {
      return null;
    }

    const estado = subscription.estado;
    const tieneMpPreapprovalId = !!subscription.mp_preapproval_id;
    const tieneIntentosFallidos = subscription.intentos_pago > 0;

    let config = null;

    if (estado === 'pending' && !tieneIntentosFallidos && tieneMpPreapprovalId) {
      config = {
        text: 'Continuar Pago',
        icon: <FiCreditCard className="h-4 w-4" />,
        action: handleContinuePayment,
        variant: 'primary',
        description: 'Completa tu suscripción pendiente'
      };
    } else if ((estado === 'pending' || tieneIntentosFallidos) && tieneMpPreapprovalId) {
      config = {
        text: 'Reintentar Pago',
        icon: <FiRefreshCw className="h-4 w-4" />,
        action: handleRetryPayment,
        variant: 'warning',
        description: subscription.ultimo_error_pago || 'Reintenta el pago de tu suscripción'
      };
    } else if (!tieneMpPreapprovalId) {
      config = {
        text: 'Completar Suscripción',
        icon: <FiCreditCard className="h-4 w-4" />,
        action: handleCreateMercadoPagoSubscription,
        variant: 'primary',
        description: 'Completa tu suscripción en Mercado Pago para activar tu plan'
      };
    }

    return config;
  }, [subscription, handleContinuePayment, handleRetryPayment, handleCreateMercadoPagoSubscription]);
  // Si no está autenticado
  if (!isAuthenticated) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 text-center">
        <p className="text-yellow-800 dark:text-yellow-200">
          Debes iniciar sesión para ver tus suscripciones
        </p>
      </div>
    );
  }

  // Si no tiene suscripción
  if (!subscription) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Estado de tu Suscripción
        </h3>
        <div className="text-center py-8">
          <div className="text-gray-500 dark:text-gray-400 mb-4">
            <FiSettings className="h-12 w-12 mx-auto mb-2" />
            <p>No tienes una suscripción activa</p>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Elige uno de los planes disponibles para comenzar a disfrutar de todas las características premium
          </p>
        </div>
      </div>
    );
  }

  // Muestra la suscripción activa
  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm p-6 mb-8">
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Tu Suscripción Actual
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          loading={loading}
        >
          <FiRefreshCw className="h-4 w-4" />
        </Button>
      </div>
      
      <SubscriptionStatus 
        subscription={subscription} 
        showDetails={true} 
        size="lg"
      />

      {/* Botones de acción para pagos pendientes o fallidos */}
      {shouldShowActionButtons && (
        <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div className="flex items-start gap-3">
            <FiAlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-1">
                Acción requerida
              </h4>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-3">
                {actionButtonConfig?.description}
              </p>
              
              {/* Mensaje de error si existe */}
              {actionError && (
                <div className="mb-3 p-2 bg-red-100 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
                  <p className="text-sm text-red-800 dark:text-red-200">{actionError}</p>
                </div>
              )}

              {/* Botón de acción */}
              <div className="flex gap-2">
                <Button
                  variant={actionButtonConfig?.variant || 'primary'}
                  size="sm"
                  onClick={actionButtonConfig?.action}
                  loading={actionLoading}
                  disabled={actionLoading}
                  className="flex items-center gap-2"
                >
                  {actionButtonConfig?.icon}
                  {actionButtonConfig?.text}
                </Button>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onRefresh}
                  loading={loading}
                  className="flex items-center gap-2"
                >
                  <FiRefreshCw className="h-4 w-4" />
                  Verificar estado
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Información adicional para suscripciones con intentos fallidos */}
      {subscription.intentos_pago > 0 && (
        <div className="mt-3 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
          <div className="flex items-center gap-2 text-sm text-orange-800 dark:text-orange-200">
            <FiRefreshCw className="h-4 w-4" />
            <span>
              Intentos de pago: <strong>{subscription.intentos_pago}</strong>
            </span>
          </div>
          {subscription.ultimo_error_pago && (
            <p className="text-xs text-orange-700 dark:text-orange-300 mt-1">
              Último error: {subscription.ultimo_error_pago}
            </p>
          )}
        </div>
      )}

      {subscription.estado === 'authorized' && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-neutral-700">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600 dark:text-gray-400">Próxima renovación</p>
              <p className="font-semibold text-gray-900 dark:text-white">
                {subscription.proxima_renovacion 
                  ? new Date(subscription.proxima_renovacion).toLocaleDateString('es-MX')
                  : 'N/A'
                }
              </p>
            </div>
            <div>
              <p className="text-gray-600 dark:text-gray-400">Método de pago</p>
              <p className="font-semibold text-gray-900 dark:text-white">
                {subscription.payment_method || 'Mercado Pago'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionCard;
